import React, { useState, useEffect, useRef } from 'react';
import ImageCacheManager from './ImageCacheManager';

/**
 * Centralized item icon rendering utility
 * Ensures consistent icon handling across all UI components
 * Provides fallback to unknown.png for missing/broken icons
 * Uses IndexedDB caching to reduce CDN bandwidth
 */

interface ItemIconProps {
  icon: string | undefined;
  name: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  disableCache?: boolean; // For testing or specific use cases
  itemId?: string; // For determining fallback chain (e.g., blueprint items)
}

const getPublicUrl = (): string => process.env.PUBLIC_URL || '';

const getIconSrc = (icon: string | undefined, itemId?: string): string => {
  const publicUrl = getPublicUrl();
  
  // If no icon provided, check if it's a blueprint item
  if (!icon) {
    if (itemId && itemId.startsWith('blueprint_')) {
      return `${publicUrl}/assets/items/blueprints/blueprint_fallback.png`;
    }
    return `${publicUrl}/assets/unknown.png`;
  }
  
  // If icon already has full path, use as-is
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    return icon;
  }
  
  // If icon starts with /assets, prepend publicUrl
  if (icon.startsWith('/assets')) {
    return `${publicUrl}${icon}`;
  }
  
  // Otherwise, treat as relative path and prepend /assets/
  return `${publicUrl}/assets/${icon}`;
};

/**
 * Render item icon with automatic fallback to unknown.png
 * This component ensures no broken image placeholders appear
 * Uses IndexedDB caching for persistent storage
 */
export const ItemIcon: React.FC<ItemIconProps> = ({
  icon,
  name,
  size = 32,
  className = 'item-img',
  style = {},
  disableCache = false,
  itemId,
}) => {
  const publicUrl = getPublicUrl();
  const fullIconUrl = getIconSrc(icon, itemId);
  const blueprintFallback = itemId && itemId.startsWith('blueprint_') ? `${publicUrl}/assets/items/blueprints/blueprint_fallback.png` : null;
  const fallbackUrl = blueprintFallback || `${publicUrl}/assets/unknown.png`;
  
  const [imageSrc, setImageSrc] = useState<string>(fullIconUrl);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isMountedRef = useRef<boolean>(true);
  const hasErroredRef = useRef<boolean>(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Reset state when icon changes
    hasErroredRef.current = false;
    setIsLoading(true);
    
    const loadImage = async () => {
      // Skip cache if disabled or for fallback images
      if (disableCache || fullIconUrl === fallbackUrl) {
        setImageSrc(fullIconUrl);
        setIsLoading(false);
        return;
      }

      try {
        // Try to get from cache
        const cachedUrl = await ImageCacheManager.fetchAndCache(fullIconUrl);
        
        if (!isMountedRef.current) return;
        
        if (cachedUrl) {
          setImageSrc(cachedUrl);
        } else {
          // Cache failed, use direct URL
          setImageSrc(fullIconUrl);
        }
      } catch (error) {
        console.warn('Error loading cached image:', error);
        if (isMountedRef.current) {
          setImageSrc(fullIconUrl);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadImage();
  }, [fullIconUrl, fallbackUrl, disableCache]);

  const handleError = (): void => {
    // Only fallback once to prevent infinite loops
    if (!hasErroredRef.current) {
      hasErroredRef.current = true;
      setImageSrc(fallbackUrl);
    }
  };

  const handleLoad = (): void => {
    setIsLoading(false);
  };

  return (
    <img
      src={imageSrc}
      alt={name}
      className={className}
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        opacity: isLoading ? 0.5 : 1,
        transition: 'opacity 0.2s ease-in-out',
        ...style,
      }}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
};

/**
 * Legacy helper for inline rendering (for backwards compatibility)
 * Prefer using <ItemIcon /> component instead
 */
export const renderItemIcon = (
  icon: string | undefined,
  name: string,
  size: number = 32,
  className: string = 'item-img',
  style: React.CSSProperties = {}
): JSX.Element => {
  return (
    <ItemIcon
      icon={icon}
      name={name}
      size={size}
      className={className}
      style={style}
    />
  );
};

export { getIconSrc };
